import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import React, { useContext, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import vendorUser from "../../assets/images/vendorLogo.svg";
import CustomLineChart from "../../components/areaGraphChart/AreaChart";
import { AuthContext } from "../../components/auth/context/Context";
import { ThemeConfig } from "../../dataTypes/ThemeConfig";
import "dayjs/locale/pt-br"; // Import the Portuguese (Brazil) locale// Import the Brazilian Portuguese locale
import { toast } from "react-toastify";

interface VendorBenefitsData {
  date: string;
  count: number;
}

interface vendorBenefits {
  themeConfig: ThemeConfig;
}

// Configure Day.js to use the Portuguese (Brazil) locale
dayjs.locale("pt-br");

export const Benefits: React.FC<vendorBenefits> = ({ themeConfig }) => {
  const location = useLocation();
  const { slug, id } = useParams<{ slug: string; id: string }>();
  const { benefitsTag } = useContext(AuthContext);
  const [getVendorBenefitCliques, setGetVendorBenefitCliques] = useState<
    VendorBenefitsData[]
  >([]);
  const [getVendorBenefitUtilizações, setGetVendorBenefitUtilizações] =
    useState<VendorBenefitsData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [cliquesLoading, setCliquesLoading] = useState<boolean>(true);
  const [utilizacoesLoading, setUtilizacoesLoading] = useState<boolean>(true);

  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(
    dayjs("2023-01-01")
  );
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(
    dayjs("2024-07-24")
  );
  const [cliquesType, setCliquesType] = useState<string>("day");
  const [utilizacoesType, setUtilizacoesType] = useState<string>("day");
  const [totalCliques, setTotalCliques] = useState<number>(0);
  const [totalUtilizacoes, setTotalUtilizacoes] = useState<number>(0);

  /** Get data from another component to show on this component */
  const state = location.state as {
    name?: string;
    path?: string;
  };
  const { name, path } = state || {};

  /** Sets a default image if the original image fails to load.  */
  const handleImgError = (e: any) => {
    e.target.src = vendorUser;
  };

  const fetchVendorBenefitsCliques = async (
    type: string,
    start: string,
    end: string
  ) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No token found");
      }
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/${slug}/vendorUser/getBenefitViewsByBenefit/${id}?type=${type}&startDate=${start}&endDate=${end}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (!response.ok) {
        toast.error(data?.error?.message);
      }
      const newChartData = data.data?.pageViews?.map((item: any) => {
        return {
          date: item?.date,
          Cliques: item?.count,
        };
      });

      setGetVendorBenefitCliques(newChartData);
      setTotalCliques(data.data.total);
    } catch (error) {
      // toast.error(error);
      console.error("Error fetching vendor benefits:", error);
    } finally {
      setCliquesLoading(false);
    }
  };
  const fetchVendorBenefitsUtilizações = async (
    type: string,
    start: string,
    end: string
  ) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No token found");
      }
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/${slug}/vendorUser/getBenefitUsesByBenefit/${id}?type=${type}&startDate=${start}&endDate=${end}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch vendor benefits");
      }
      const data = await response.json();
      const newChartData = data.data?.benefitUses?.map((item: any) => {
        return {
          date: item?.date,
          Utilizações: item?.count,
        };
      });
      setGetVendorBenefitUtilizações(newChartData);
      setTotalUtilizacoes(data.data.total);
    } catch (error) {
      console.error("Error fetching vendor benefits:", error);
    } finally {
      setUtilizacoesLoading(false);
    }
  };

  /**this  useEffect triggers the fetchVendorBenefitsCliques function whenever the cliquesType state changes. */
  useEffect(() => {
    setCliquesLoading(true);
    fetchVendorBenefitsCliques(
      cliquesType,
      startDate!.format("YYYY-MM-DD"),
      endDate!.format("YYYY-MM-DD")
    );
  }, [cliquesType]);

  /**this  useEffect triggers the fetchVendorBenefitsUtilizações function whenever the cliquesType state changes. */
  useEffect(() => {
    setUtilizacoesLoading(true);
    fetchVendorBenefitsUtilizações(
      utilizacoesType,
      startDate!.format("YYYY-MM-DD"),
      endDate!.format("YYYY-MM-DD")
    );
  }, [utilizacoesType]);

  /**  Fetches both clicks and uses data based on current filters. */
  const handleFilter = () => {
    setCliquesLoading(true);
    setUtilizacoesLoading(true);
    fetchVendorBenefitsCliques(
      cliquesType,
      startDate!.format("YYYY-MM-DD"),
      endDate!.format("YYYY-MM-DD")
    );
    fetchVendorBenefitsUtilizações(
      utilizacoesType,
      startDate!.format("YYYY-MM-DD"),
      endDate!.format("YYYY-MM-DD")
    );
  };

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        gap={4}
        padding="30px"
        sx={{ bgcolor: "#fff", margin: "54px 57px 0px 57px" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "40px" }}>
          <img
            src={path}
            alt=""
            style={{ width: "210px", marginBottom: "0px" }}
            onError={handleImgError}
          />
          <Box>
            <Typography
              sx={{ fontSize: "30px", fontWeight: 700, paddingBottom: "18px" }}
            >
              {name}
            </Typography>
            <Box sx={{ display: "flex", gap: "8px" }}>
              {" "}
              {benefitsTag?.length > 0 &&
                benefitsTag?.map((label: any) => {
                  return (
                    <Chip
                      // color="primary"
                      sx={{
                        bgcolor: label.color,
                        borderRadius: "18px",
                        padding: "1px 8px",
                        // color: "black",
                        textTransform: "capitalize",
                        display: "flex",
                        gap: "4px",
                        alignItems: "center",
                        width: "max-content",
                        "& .MuiChip-label": {
                          fontSize: "15px",
                          fontWeight: "medium",
                          padding: "0px 8px",
                        },
                      }}
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            gap: "4px",
                            alignItems: "center",
                          }}
                        >
                          {label.label}
                        </Box>
                      }
                    />
                  );
                })}
            </Box>
          </Box>
        </Box>
        <Box>
          <Typography sx={{ fontSize: "30px", fontWeight: 700 }}>
            Filtre o período desejado:
          </Typography>
          <div style={{ display: "flex", gap: "30px", marginTop: "20px" }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="De"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { variant: "standard" } }}
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Até"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { variant: "standard" } }}
              />
            </LocalizationProvider>
            <Button
              type="submit"
              variant="contained"
              // color="inherit"
              // color=""
              sx={{
                bgcolor: themeConfig.color,
                // color: themeConfig.color === "#000000" ? "#fff" : "#000000",
                height: "45px",
                fontSize: "21px",
                fontWeight: 700,
                textTransform: "capitalize",
                borderRadius: "8px",
                // padding: "10px 35px",
                width: "148px",
                "&:hover": {
                  bgcolor: themeConfig.color,
                },
              }}
              onClick={handleFilter}
            >
              Filtrar
            </Button>
          </div>
        </Box>
      </Box>
      <Box
        justifyContent="space-between"
        gap={4}
        padding="30px"
        sx={{ bgcolor: "#fff", margin: "20px 57px" }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "30px", fontWeight: 700 }}>
            Cliques: {totalCliques}
          </Typography>
          <Box>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 400, color: "grey" }}
            >
              Visualizar por
            </Typography>
            <Select
              value={cliquesType}
              onChange={(e) => setCliquesType(e.target.value)}
              sx={{ width: "120px" }}
              variant="standard"
            >
              <MenuItem value="day">Dia</MenuItem>
              <MenuItem value="month">Mês</MenuItem>
            </Select>
          </Box>
        </Box>

        <div style={{ display: "flex", gap: "42px" }}>
          {cliquesLoading ? (
            <CircularProgress
              style={{
                display: "flex",
                justifyContent: "center",
                margin: "auto",
              }}
            />
          ) : (
            <CustomLineChart
              keyName={"Cliques"}
              data={getVendorBenefitCliques}
            />
          )}
        </div>
      </Box>
      <Box
        justifyContent="space-between"
        gap={4}
        padding="30px"
        sx={{ bgcolor: "#fff", margin: "20px 57px" }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "30px", fontWeight: 700 }}>
            Utilizações: {totalUtilizacoes}
          </Typography>
          <Box>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 400, color: "grey" }}
            >
              Visualizar por
            </Typography>
            <Select
              value={utilizacoesType}
              onChange={(e) => setUtilizacoesType(e.target.value)}
              sx={{ width: "120px" }}
              variant="standard"
            >
              <MenuItem value="hour">Hora</MenuItem>
              <MenuItem value="day">Dia</MenuItem>
              <MenuItem value="month">Mês</MenuItem>
            </Select>
          </Box>
        </Box>
        {utilizacoesLoading ? (
          <CircularProgress
            style={{
              display: "flex",
              justifyContent: "center",
              margin: "auto",
            }}
          />
        ) : (
          <CustomLineChart
            data={getVendorBenefitUtilizações}
            keyName={"Utilizações"}
          />
        )}
      </Box>
    </>
  );
};
